import { createBurnInstruction, TOKEN_PROGRAM_ID } from "@solana/spl-token0.3.8";
import type { Connection } from "@solana/web3.js";
import { PublicKey } from "@solana/web3.js";
import { fromLamports } from "@stepfinance/common";
import type { NormalizedTokenAccount } from "@stepfinance/tokens";

import { ReactComponent as BurnIcon } from "../../assets/flame.svg";
import { TokenChangeNotificationDisplay } from "../../components/Notifications/TokenChangeNotificationDisplay";
import { confirmOptions } from "../../constants/transaction";
import type { BurnNotificationStrings } from "../../contexts/language/languageTypes";
import type { ConnectedWallet } from "../../hooks";
import type { NFTWithTokenAccount } from "../../models/nfts/nfts";
import { getBurnNFTNotifications } from "../../utils/nft/utils";
import { getTokenByMint } from "../../utils/tokenInfo";
import type { NotificationContentStates } from "../../utils/transactions";
import { sendTransaction } from "../../utils/transactions";
import { formatStringWithPlaceholders, isFungibleToken } from "../../utils/utils";
import { closeTokenAccountsTx } from "../account";

export interface TokenData {
  pubKey: string;
  owner: string;
  mint: string;
  decimals: number;
  url?: string;
  name: string;
}

export const STEP_BURN_FEE = 0.05;

export const burnToken = async (
  connection: Connection,
  wallet: ConnectedWallet,
  burnAmount: number,
  tokenData: NFTWithTokenAccount | NormalizedTokenAccount,
  notificationStrings: BurnNotificationStrings,
): Promise<any> => {
  const normalizedData = normalizeToken(tokenData);
  const tokenAccountKey = new PublicKey(normalizedData.pubKey);

  const createCloseAccountInstructions = async () => {
    const tokenAccountBalance = await connection.getTokenAccountBalance(tokenAccountKey);
    const tokenAccountRemainingBalance = (tokenAccountBalance.value.uiAmount ?? 0) - burnAmount;

    const isTokenAccountEmpty = Number(tokenAccountBalance.value.amount) === 0 || tokenAccountRemainingBalance === 0;
    if (isTokenAccountEmpty) {
      const transaction = await closeTokenAccountsTx(connection, wallet.publicKey, [tokenAccountKey]);
      return transaction.instructions;
    }
    return [];
  };

  const createBurnSPLInstructions = () => {
    const burnInstruction = createBurnInstruction(
      new PublicKey(normalizedData.pubKey),
      new PublicKey(normalizedData.mint),
      new PublicKey(normalizedData.owner),
      burnAmount * 10 ** normalizedData.decimals,
      [],
      TOKEN_PROGRAM_ID,
    );
    return burnInstruction;
  };

  const notifications = isFungibleToken(tokenData)
    ? getBurnTokenNotifications(normalizedData.name, burnAmount, normalizedData.mint, notificationStrings)
    : getBurnNFTNotifications(normalizedData.name, burnAmount, normalizedData.mint, notificationStrings);

  return await sendTransaction(
    connection,
    wallet,
    [createBurnSPLInstructions(), ...(await createCloseAccountInstructions())],
    [],
    true,
    confirmOptions,
    true,
    notifications,
  );
};

export const getRedeemableTokenBalance = async (connection: Connection, tokenAccount: PublicKey, afterFees = false) => {
  const lamports = await connection.getBalance(tokenAccount);
  const solAmount = fromLamports(lamports, 9);
  const reclaimBalanceAfterFees = parseFloat((solAmount - solAmount * STEP_BURN_FEE).toFixed(9));
  return afterFees ? reclaimBalanceAfterFees : solAmount;
};

export const getBurnTokenNotifications = (
  name: string,
  amount: number,
  mintAddress: string,
  notificationStrings: BurnNotificationStrings,
): NotificationContentStates => {
  return {
    inProgress: {
      message: formatStringWithPlaceholders(notificationStrings.burningTokenName, name),
      description: notificationStrings.transactionInProgress,
    },
    success: {
      icon: <BurnIcon />,
      message: notificationStrings.burnSuccessful,
      description: (
        <>
          <span>
            {formatStringWithPlaceholders(
              notificationStrings.youDidAction.message,
              notificationStrings.youDidAction.burnt,
            )}
          </span>
          <TokenChangeNotificationDisplay amount={amount} mintAddress={mintAddress} type={"decrease"} />
        </>
      ),
    },
  };
};

const normalizeToken = (data: NFTWithTokenAccount | NormalizedTokenAccount): TokenData => {
  if (isFungibleToken(data)) {
    return {
      decimals: data.tokenAmount.decimals,
      mint: data.info.mint,
      owner: data.info.owner,
      pubKey: data.pubkey,
      name: getTokenByMint(data.info.mint)?.name ?? "",
    };
  } else {
    return {
      decimals: data.associatedTokenAccount.tokenAmount.decimals ?? 0,
      mint: data.address.toBase58(),
      owner: data.associatedTokenAccount.info.owner,
      pubKey: data.associatedTokenAccount.pubkey,
      name: data.name ?? "",
      url: data.json?.image ? data.json.image : "",
    };
  }
};
